import React, { useState, useEffect, useCallback } from 'react';
import DataService from '../../services/DataService';
import useNotification from '../../hooks/useNotification';
import TimeStamp from '../../utils/TimeStamp';
import '../../styles/Modal.css';

const BillLineModal = ({ isActive, onClose, billId }) => {
    const [bill, setBill] = useState(null);
    const [billLines, setBillLines] = useState([]);
    const [dueDateInput, setDueDateInput] = useState(''); // Nouvel état pour la saisie
    const [isValidDueDate, setIsValidDueDate] = useState(false);
    const [dueDateError, setDueDateError] = useState('');
    const { showNotification } = useNotification();

    const validateDueDate = (dateStr) => {
        if (!dateStr || dateStr.trim() === '') {
            setDueDateError('La date d\'échéance est obligatoire');
            setIsValidDueDate(false);
            return false;
        }

        // Vérifie le format YYYY-MM-DD
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(dateStr)) {
            setDueDateError('Format de date invalide (AAAA-MM-JJ)');
            setIsValidDueDate(false);
            return false;
        }

        try {
            // Convertir la date string en TimeStamp
            const [year, month, day] = dateStr.split('-').map(num => parseInt(num));
            const inputDate = new TimeStamp(year, month, day);
            const today = TimeStamp.fromDate(new Date());

            // Vérifier que la date est supérieure à aujourd'hui
            if (TimeStamp.compare(inputDate, today) <= 0) {
                setDueDateError('La date d\'échéance doit être supérieure à la date du jour');
                setIsValidDueDate(false);
                return false;
            }

            setDueDateError('');
            setIsValidDueDate(true);
            return true;
        } catch (error) {
            setDueDateError('Date invalide');
            setIsValidDueDate(false);
            return false;
        }
    };

    const loadBillLines = useCallback(async () => {
        try {
            const billWithLines = await DataService.getBillWithLines(billId);
            if (billWithLines) {
                // Initialiser la progression avec lastProgess pour chaque ligne
                const initializeProgress = (lines) => {
                    return lines.map(line => ({
                        ...line,
                        progress: line.progress || line.lastProgess || 0,
                        subLines: line.subLines ? initializeProgress(line.subLines) : []
                    }));
                };

                setBill(billWithLines);
                setBillLines(initializeProgress(billWithLines.lines || []));
            }
        } catch (error) {
            showNotification('Erreur lors du chargement des lignes', 'error');
        }
    }, [billId, showNotification]);

    useEffect(() => {
        if (isActive && billId) {
            loadBillLines();
        }
    }, [isActive, billId, loadBillLines]);

    useEffect(() => {
        if (bill?.dueDate) {
            const dateStr = `${bill.dueDate.year}-${String(bill.dueDate.month).padStart(2, '0')}-${String(bill.dueDate.day).padStart(2, '0')}`;
            setDueDateInput(dateStr);
            validateDueDate(dateStr);
        } else {
            // Si pas de date d'échéance, la validation échouera
            validateDueDate('');
        }
    }, [bill]);

    const handleDueDateChange = async (dateStr) => {
        setDueDateInput(dateStr); // Mettre à jour l'état de saisie
        validateDueDate(dateStr); // Valider en temps réel

        try {
            // Vérifie que la date est au format YYYY-MM-DD
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(dateStr)) {
                return; // Sort silencieusement si le format n'est pas encore complet
            }

            // Convertir la date string (YYYY-MM-DD) en TimeStamp
            const [year, month, day] = dateStr.split('-').map(num => parseInt(num));
            const dueDate = new TimeStamp(year, month, day);

            const updatedBill = await DataService.update('bills', billId, { 
                ...bill,
                dueDate 
            });
            setBill(updatedBill);
            showNotification('Date d\'échéance mise à jour avec succès', 'success');
        } catch (error) {
            showNotification('Erreur lors de la mise à jour de la date d\'échéance', 'error');
        }
    };

    const handleClose = () => {
        if (!isValidDueDate) {
            showNotification('Impossible de fermer : la date d\'échéance doit être renseignée et supérieure à la date du jour', 'error');
            return;
        }
        onClose();
    };

    const handleCommandChange = async (command) => {
        try {
            const updatedBill = await DataService.update('bills', billId, { 
                ...bill,
                command 
            });
            setBill(updatedBill);
            showNotification('Référence commande mise à jour', 'success');
        } catch (error) {
            showNotification('Erreur lors de la mise à jour de la référence', 'error');
        }
    };

    const updateLine = async (lineId, progress) => {
        try {
            const line = findLineById(billLines, lineId);
            if (!line) return;

            // Utiliser lastProgess si progress n'est pas défini
            const newProgress = progress || line.lastProgess || 0;

            // Mettre à jour la ligne localement
            const updatedLine = {
                ...line,
                progress: Math.min(100, Math.max(0, newProgress))
            };
            
            // Mettre à jour billLines avec la nouvelle progression
            const updatedBillLines = billLines.map(bl => {
                if (bl.id === lineId) {
                    return updatedLine;
                }
                if (bl.subLines) {
                    bl.subLines = bl.subLines.map(sl =>
                        sl.id === lineId ? updatedLine : sl
                    );
                }
                return bl;
            });

            // Calculer les montants avec les valeurs mises à jour
            const totalProgressAmount = calculateTotalAmount(updatedBillLines);
            const currentBillAmount = totalProgressAmount - (bill?.alreadyPaid || 0);

            // Mettre à jour la base de données
            await DataService.update('billLines', lineId, updatedLine);
            await DataService.update('bills', billId, {
                currentBillAmount: currentBillAmount,
                currentProgressAmount: totalProgressAmount
            });

            await loadBillLines();
            showNotification('Progression mise à jour', 'success');
        } catch (error) {
            showNotification('Erreur lors de la mise à jour de la progression', 'error');
        }
    };

    const findLineById = (lines, lineId) => {
        for (const line of lines) {
            if (line.id === lineId) return line;
            if (line.subLines) {
                const found = findLineById(line.subLines, lineId);
                if (found) return found;
            }
        }
        return null;
    };

    const calculateTotalPrice = (lines) => {
        return lines.reduce((total, line) => {
            const lineTotal = line.price || 0;
            const subLinesTotal = line.subLines ? calculateTotalPrice(line.subLines) : 0;
            return total + lineTotal + subLinesTotal;
        }, 0);
    };

    const calculateTotalAmount = (lines) => {
        return lines.reduce((total, line) => {
            const lineAmount = line.price * (line.progress || 0) / 100;
            const subLinesAmount = line.subLines ? calculateTotalAmount(line.subLines) : 0;
            return total + lineAmount + subLinesAmount;
        }, 0);
    };

    const renderLineRow = (line, isSubLine = false) => (
        <tr key={line.id}>
            <td>
                <span style={{ 
                    paddingLeft: isSubLine ? '20px' : '0',
                    fontWeight: isSubLine ? 'normal' : 'bold',
                    fontSize: isSubLine ? '0.90rem' : '1.10rem'
                }}>
                    {line.description}
                </span>
            </td>
            <td style={{ textAlign: 'right' }}>
                {!isSubLine ? `${line.price.toFixed(2)} €` : ''}
            </td>
            <td style={{ textAlign: 'right' }}>
                {!isSubLine ? `${(line.lastProgess || 0).toFixed(2)} %` : ''}
            </td>
            <td>
                {!isSubLine && (
                    <div className="control is-expanded">
                        <input
                            className="input"
                            type="number"
                            min={line.lastProgess || 0}
                            max="100"
                            value={line.progress || line.lastProgess || 0}
                            onChange={(e) => updateLine(line.id, parseFloat(e.target.value))}
                            disabled={!line.hasOwnProperty('progress') || (line.lastProgess || 0) >= 100}
                            style={{ 
                                backgroundColor: (line.lastProgess || 0) >= 100 ? '#f5f5f5' : 'transparent',
                                fontSize: '1rem'
                            }}
                        />
                    </div>
                )}
            </td>
            <td style={{ textAlign: 'right' }}>
                {!isSubLine ? `${(line.price * line.progress / 100).toFixed(2)} €` : ''}
            </td>
        </tr>
    );

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={handleClose}></div>
            <div className="modal-card modal-card-wide">
                <header className="modal-card-head">
                    <p className="modal-card-title">Lignes de facture</p>
                    <button className="delete" aria-label="close" onClick={handleClose}></button>
                </header>
                <section className="modal-card-body">
                    <div className="modal-subpanel">
                        <div className="modal-subpanel-header">
                            <span>Information générale</span>
                        </div>
                        <div className="table-container">
                            <table className="table is-fullwidth is-striped is-hoverable mb-0">
                                <tbody>
                                    <tr>
                                        <td style={{ width: '170px' }}>
                                            <label className="label has-text-weight-bold">Date d'échéance</label>
                                        </td>
                                        <td>
                                            <div className="control">
                                                <input
                                                    className={`input ${!isValidDueDate && dueDateInput ? 'is-danger' : ''}`}
                                                    type="text"
                                                    placeholder="AAAA-MM-JJ"
                                                    value={dueDateInput}
                                                    onChange={(e) => handleDueDateChange(e.target.value)}
                                                />
                                                {dueDateError && (
                                                    <p className="help is-danger" style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                                                        {dueDateError}
                                                    </p>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <label className="label has-text-weight-bold">Sommes déjà payées</label>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {(bill?.alreadyPaid || 0).toFixed(2)} €
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ width: '200px' }}>
                                            <label className="label has-text-weight-bold">Référence commande</label>
                                        </td>
                                        <td>
                                            <input
                                                className="input"
                                                type="text"
                                                value={bill?.command || ''}
                                                onChange={(e) => handleCommandChange(e.target.value)}
                                                placeholder="Référence commande"
                                            />
                                        </td>
                                        <td>
                                            <label className="label has-text-weight-bold">Montant facture en cours</label>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {(calculateTotalAmount(billLines) - (bill?.alreadyPaid || 0)).toFixed(2)} €
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <label className="label has-text-weight-bold">Montant total</label>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {calculateTotalPrice(billLines).toFixed(2)} €
                                        </td>
                                        <td>
                                            <label className="label has-text-weight-bold">Progression</label>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            {calculateTotalAmount(billLines).toFixed(2)} €
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="modal-subpanel">
                        <div className="modal-subpanel-header">
                            <span>Lignes</span>
                        </div>
                        <div className="table-container">
                            <table className="table is-fullwidth is-striped is-hoverable mb-0">
                                <thead className="modal-table-header">
                                    <tr>
                                        <th>Description</th>
                                        <th style={{ width: '120px' }}>Prix</th>
                                        <th style={{ width: '120px' }}>Prec. prog.</th>
                                        <th style={{ width: '200px' }}>Progression (%)</th>
                                        <th style={{ width: '120px' }}>Montant</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {billLines.map(line => (
                                        <React.Fragment key={line.id}>
                                            {renderLineRow(line)}
                                            {line.subLines?.map(subLine => (
                                                renderLineRow(subLine, true)
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
                <footer className="modal-card-foot">
                    <button 
                        className={`button ${!isValidDueDate ? 'is-disabled' : ''}`}
                        onClick={handleClose}
                        disabled={!isValidDueDate}
                        title={!isValidDueDate ? 'Veuillez saisir une date d\'échéance valide' : ''}
                    >
                        Fermer
                    </button>
                    {!isValidDueDate && (
                        <p className="help is-danger" style={{ fontSize: '0.85rem', marginLeft: '10px', alignSelf: 'center' }}>
                            Une date d'échéance valide est requise pour fermer
                        </p>
                    )}
                </footer>
            </div>
        </div>
    );
};

export default BillLineModal;
